import type { ApiResponse } from '@/libs/interfaces'
import type {
  WorkLog,
  WorkLogListParams,
  WorkLogListResponse,
  WorkLogSummary,
} from '@/services/api/workLogs'

import { mockWorkLogs } from '../fixtures/workLogs'
import { delay, genID, nowISO } from '../helpers'
import { createEntityStore } from '../store'

const workLogStore = createEntityStore<WorkLog, 'id'>(mockWorkLogs, 'id')

const toResponse = <T>(data: T): ApiResponse<T> => ({ data, status: 200 })

const filterWorkLogs = (params?: WorkLogListParams): WorkLog[] =>
  workLogStore
    .getAll()
    .filter((workLog) => (params?.employeeId ? workLog.employeeId === params.employeeId : true))
    .filter((workLog) => (params?.dateFrom ? workLog.date >= params.dateFrom : true))
    .filter((workLog) => (params?.dateTo ? workLog.date <= params.dateTo : true))

export const workLogsApiMock = {
  list: async (params?: WorkLogListParams): Promise<ApiResponse<WorkLogListResponse>> => {
    const filtered = filterWorkLogs(params)
    const page = params?.page ?? 1
    const limit = params?.limit ?? filtered.length
    const start = (page - 1) * limit
    return delay(
      toResponse({
        data: filtered.slice(start, start + limit),
        total: filtered.length,
        page,
        limit,
      }),
    )
  },

  getById: async (id: string): Promise<ApiResponse<WorkLog>> =>
    delay(toResponse(workLogStore.getById(id) ?? ({} as WorkLog))),

  create: async (data: Omit<WorkLog, 'id'>): Promise<ApiResponse<WorkLog>> => {
    const workLog: WorkLog = {
      id: genID('work-log'),
      createdAt: nowISO(),
      updatedAt: nowISO(),
      ...data,
    }
    workLogStore.insert(workLog)
    return delay(toResponse(workLog))
  },

  update: async (id: string, data: Partial<WorkLog>): Promise<ApiResponse<WorkLog>> => {
    const updated = workLogStore.update(id, { ...data, updatedAt: nowISO() })
    return delay(toResponse(updated ?? ({ ...data, id } as WorkLog)))
  },

  delete: async (id: string): Promise<ApiResponse<void>> => {
    workLogStore.remove(id)
    return delay(toResponse(undefined))
  },

  getTodaySummary: async (): Promise<ApiResponse<WorkLogSummary>> => {
    const today = new Date().toISOString().slice(0, 10)
    const todaysLogs = workLogStore.getAll().filter((workLog) => workLog.date === today)
    const totalHours = todaysLogs.reduce((sum, workLog) => sum + (workLog.totalHours ?? 0), 0)
    const employeesClocked = new Set(todaysLogs.map((workLog) => workLog.employeeId)).size
    return delay(
      toResponse({
        totalHours,
        employeesClocked,
        avgHoursPerEmployee: employeesClocked > 0 ? totalHours / employeesClocked : 0,
      }),
    )
  },

  getEmployeeSummary: async (
    employeeId: string,
    dateFrom?: string,
    dateTo?: string,
  ): Promise<ApiResponse<WorkLogSummary>> => {
    const employeeLogs = workLogStore
      .getAll()
      .filter((workLog) => workLog.employeeId === employeeId)
      .filter((workLog) => (dateFrom ? workLog.date >= dateFrom : true))
      .filter((workLog) => (dateTo ? workLog.date <= dateTo : true))
    const totalHours = employeeLogs.reduce((sum, workLog) => sum + (workLog.totalHours ?? 0), 0)
    return delay(
      toResponse({
        totalHours,
        employeesClocked: employeeLogs.length > 0 ? 1 : 0,
        avgHoursPerEmployee: totalHours,
      }),
    )
  },
}
